import "@dxos/node-std/globals"
import {
  FeedFactory,
  FeedStore,
  FeedWrapper
} from "./chunk-6Z3M43BG.mjs";

// packages/common/feed-store/src/feed-iterator.ts
import assert2 from "@dxos/node-std/assert";
import { Trigger as Trigger2 } from "@dxos/async";
import { log as log2 } from "@dxos/log";

// packages/common/feed-store/src/feed-queue.ts
import assert from "@dxos/node-std/assert";
import { inspect } from "@dxos/node-std/util";
import { Writable } from "streamx";
import { Event, latch, Trigger } from "@dxos/async";
import { inspectObject } from "@dxos/debug";
import { log } from "@dxos/log";
var defaultReadStreamOptions = {
  live: true
  // Keep reading until closed.
};
var FeedQueue = class {
  // prettier-ignore
  constructor(_feed, _options = {}) {
    this._feed = _feed;
    this._options = _options;
    this.updated = new Event();
    this._messageTrigger = new Trigger({
      autoReset: true
    });
    this._currentBlock = void 0;
    this._index = -1;
  }
  [inspect.custom]() {
    return inspectObject(this);
  }
  toJSON() {
    return {
      feedKey: this._feed.key,
      index: this.index,
      length: this.length,
      open: this.isOpen
    };
  }
  get feed() {
    return this._feed;
  }
  get isOpen() {
    return Boolean(this._feedConsumer);
  }
  get length() {
    return this._feed.properties.length;
  }
  /**
  * Index (seq) of the NEXT block to be read, or -1 if not open.
  */
  get index() {
    return this._index;
  }
  /**
  * Opens (or reopens) the queue.
  * @param options.start Starting index. First mutation to be read would have `seq == options.start`.
  */
  async open(options = {}) {
    var _a;
    if (this.isOpen) {
      return;
    }
    this._index = (_a = options.start) != null ? _a : 0;
    log("opening", {
      feedKey: this._feed.key
    }, {
      file: "feed-queue.ts",
      line: 91,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const opts = Object.assign({}, defaultReadStreamOptions, options);
    const feedStream = this._feed.createReadableStream(opts);
    this._feedConsumer = new Writable({
      write: (data, next) => {
        this._next = () => {
          this._next = void 0;
          this._currentBlock = void 0;
          this._index++;
          next();
        };
        this._currentBlock = {
          feedKey: this._feed.key,
          seq: this._index,
          data
        };
        this._messageTrigger.wake(this._currentBlock);
        this.updated.emit(this);
      }
    });
    const onClose = () => {
      if (this._feedConsumer) {
        log("queue closed", {
          feedKey: this._feed.key
        }, {
          file: "feed-queue.ts",
          line: 119,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        this._feedConsumer = void 0;
        this._next = void 0;
        this._currentBlock = void 0;
        this._index = -1;
      }
    };
    this._feed.core.once("close", () => {
      log("feed closed", {
        feedKey: this._feed.key
      }, {
        file: "feed-queue.ts",
        line: 129,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      onClose();
    });
    this._feedConsumer.once("close", () => {
      onClose();
    });
    feedStream.pipe(this._feedConsumer, () => {
      onClose();
    });
    log("opened", {}, {
      file: "feed-queue.ts",
      line: 143,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  /**
  * Closes the queue.
  */
  async close() {
    var _a;
    if (this.isOpen) {
      assert(this._feedConsumer);
      assert(!this._feed.properties.closed);
      log("closing", {
        feedKey: this._feed.key
      }, {
        file: "feed-queue.ts",
        line: 154,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      const [closed, setClosed] = latch();
      this._feedConsumer.once("close", setClosed);
      this._feedConsumer.destroy();
      (_a = this._next) == null ? void 0 : _a.call(this);
      await closed();
      log("closed", {}, {
        file: "feed-queue.ts",
        line: 160,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
  }
  /**
  * Get the block at the head of the queue without removing it.
  */
  peek() {
    return this._currentBlock;
  }
  /**
  * Pop block at the head of the queue.
  */
  async pop() {
    var _a;
    if (!this.isOpen) {
      throw new Error(`Queue closed: ${this.feed.key.truncate()}`);
    }
    let block = this.peek();
    if (!block) {
      block = await this._messageTrigger.wait();
    }
    if (block) {
      (_a = this._next) == null ? void 0 : _a.call(this);
    }
    return block;
  }
};

// packages/common/feed-store/src/feed-iterator.ts
var AbstractFeedIterator = class {
  constructor() {
    this._stopTrigger = new Trigger2();
    this._open = false;
    this._running = false;
  }
  toJSON() {
    return {
      open: this.isOpen,
      running: this.isRunning
    };
  }
  get isOpen() {
    return this._open;
  }
  get isRunning() {
    return this._running;
  }
  async open() {
    if (!this._open) {
      log2("opening...", {}, {
        file: "feed-iterator.ts",
        line: 40,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      await this._onOpen();
      this._open = true;
      await this.start();
      log2("opened", {}, {
        file: "feed-iterator.ts",
        line: 45,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
  }
  async close() {
    if (this._open) {
      log2("closing...", {}, {
        file: "feed-iterator.ts",
        line: 51,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      await this.stop();
      await this._onClose();
      this._open = false;
      log2("closed", {}, {
        file: "feed-iterator.ts",
        line: 56,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
  }
  async start() {
    assert2(this._open);
    if (!this._running) {
      this._running = true;
    }
  }
  async stop() {
    assert2(this._open);
    if (this._running) {
      this._running = false;
      this._stopTrigger.wake();
    }
  }
  //
  // AsyncIterable
  //
  [Symbol.asyncIterator]() {
    return this._generator();
  }
  async *_generator() {
    log2("started", {}, {
      file: "feed-iterator.ts",
      line: 84,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    while (this._running) {
      const block = await Promise.race([
        this._stopTrigger.wait(),
        this._nextBlock()
      ]);
      if (block === void 0) {
        break;
      }
      yield block;
    }
    log2("stopped", {}, {
      file: "feed-iterator.ts",
      line: 95,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
};
var FeedIterator = class extends AbstractFeedIterator {
  // prettier-ignore
  constructor(_feed) {
    super();
    this._feed = _feed;
    this._queue = new FeedQueue(this._feed);
  }
  async _onOpen() {
    await this._queue.open();
  }
  async _onClose() {
    await this._queue.close();
  }
  async _nextBlock() {
    return this._queue.pop();
  }
};

// packages/common/feed-store/src/feed-set-iterator.ts
import assert3 from "@dxos/node-std/assert";
import { inspect as inspect2 } from "@dxos/node-std/util";
import { Event as Event2, EventSubscriptions, Trigger as Trigger3 } from "@dxos/async";
import { inspectObject as inspectObject2 } from "@dxos/debug";
import { PublicKey } from "@dxos/keys";
import { log as log3 } from "@dxos/log";
import { ComplexMap, isNotNullOrUndefined } from "@dxos/util";
var defaultFeedSetIteratorOptions = {
  stallTimeout: 1e3
};
var FeedSetIterator = class extends AbstractFeedIterator {
  constructor(_selector, options = defaultFeedSetIteratorOptions) {
    super();
    this._selector = _selector;
    this.options = options;
    this._feedQueues = new ComplexMap(PublicKey.hash);
    this._trigger = new Trigger3({
      autoReset: true
    });
    this._subscriptions = new EventSubscriptions();
    this.stalled = new Event2();
    assert3(_selector);
    assert3(options);
  }
  [inspect2.custom]() {
    return inspectObject2(this);
  }
  toJSON() {
    return {
      open: this.isOpen,
      running: this.isRunning,
      indexes: this.indexes
    };
  }
  get size() {
    return this._feedQueues.size;
  }
  get feeds() {
    return Array.from(this._feedQueues.values()).map((feedQueue) => feedQueue.feed);
  }
  get indexes() {
    return Array.from(this._feedQueues.values()).map((feedQueue) => ({
      feedKey: feedQueue.feed.key,
      index: feedQueue.index
    }));
  }
  async addFeed(feed) {
    var _a, _b;
    assert3(!this._feedQueues.has(feed.key), `Feed already added: ${feed.key}`);
    assert3(feed.properties.opened);
    log3("feed added", {
      feedKey: feed.key
    }, {
      file: "feed-set-iterator.ts",
      line: 89,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const queue = new FeedQueue(feed);
    this._feedQueues.set(feed.key, queue);
    this._subscriptions.add(queue.updated.on(() => {
      this._trigger.wake();
    }));
    await queue.open({
      start: (_b = (_a = this.options.start) == null ? void 0 : _a.find((index) => index.feedKey.equals(feed.key))) == null ? void 0 : _b.index
    });
    this._trigger.wake();
  }
  hasFeed(feedKey) {
    return this._feedQueues.has(feedKey);
  }
  async _onOpen() {
    for (const queue of this._feedQueues.values()) {
      await queue.open();
    }
  }
  async _onClose() {
    this._subscriptions.clear();
    await Promise.all(Array.from(this._feedQueues.values()).map((queue) => queue.close()));
    this._trigger.wake();
  }
  /**
  * Gets the next block from the selected queue.
  */
  async _nextBlock() {
    let t;
    while (this._running) {
      const queues = Array.from(this._feedQueues.values());
      const blocks = queues.map((queue) => queue.peek()).filter(isNotNullOrUndefined);
      if (blocks.length) {
        const idx = this._selector(blocks);
        log3("selected", {
          idx,
          blocks
        }, {
          file: "feed-set-iterator.ts",
          line: 139,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        if (idx === void 0) {
          if (t === void 0) {
            t = setTimeout(() => {
              this.stalled.emit(this);
            }, this.options.stallTimeout);
          }
        } else {
          if (t !== void 0) {
            clearTimeout(t);
            t = void 0;
          }
          if (idx >= blocks.length) {
            throw new Error(`Index out of bounds: ${idx} of ${blocks.length}`);
          }
          const queue = this._feedQueues.get(blocks[idx].feedKey);
          log3("popping", queue.toJSON(), {
            file: "feed-set-iterator.ts",
            line: 158,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          try {
            const message = await queue.pop();
            assert3(message === blocks[idx]);
            return message;
          } catch (err) {
            log3.warn("queue closed", {
              feedKey: queue.feed.key
            }, {
              file: "feed-set-iterator.ts",
              line: 165,
              scope: this,
              callSite: (f, a) => f(...a)
            });
            console.log(Array.from(this._feedQueues.values()));
          }
        }
      }
      await this._trigger.wait();
    }
  }
};

// packages/common/feed-store/src/feed-writer.ts
var createFeedWriter = (cb) => ({
  write: async (data) => {
    return cb(data);
  }
});
var writeMessages = async (writer, messages) => {
  const receipts = [];
  for (const message of messages) {
    receipts.push(await writer.write(message));
  }
  return receipts;
};
export {
  AbstractFeedIterator,
  FeedFactory,
  FeedIterator,
  FeedQueue,
  FeedSetIterator,
  FeedStore,
  FeedWrapper,
  createFeedWriter,
  defaultFeedSetIteratorOptions,
  defaultReadStreamOptions,
  writeMessages
};
//# sourceMappingURL=index.mjs.map
